/**
 * Pure engine glue shared by `LessonController` — builds the starting state for
 * a script and translates a chosen call into an engine transition. Kept out of
 * the controller so that file stays focused on step gating and under the size
 * cap. No DOM, no timers.
 */

import {
  claimChi,
  claimPung,
  createHand,
  createHandFromDeal,
  declareRon,
  type MahjongState,
} from '../game/GameState.js';
import type { ChiOption, Claim, SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { ClaimKind, LessonScript } from './types.js';

/** Materialises a script's opening state — a crafted deal if present, else a seed. */
export function buildLessonState(script: LessonScript): MahjongState {
  if (script.deal !== undefined) {
    return createHandFromDeal(script.deal.hands, script.deal.wall, script.seed);
  }
  if (script.seed !== undefined) return createHand(script.seed);
  throw new Error(`lesson '${script.id}' has neither a deal nor a seed`);
}

/**
 * A concrete `Claim` for a learner's chosen call, or null when that call is not
 * actually available (an illegal attempt the controller then rejects).
 */
export function buildClaim(
  seat: Seat,
  kind: ClaimKind,
  claims: SeatClaims,
  option?: ChiOption,
): Claim | null {
  switch (kind) {
    case 'ron':
      return claims.canRon ? { type: 'ron', seat } : null;
    case 'pung':
      return claims.canPung ? { type: 'pung', seat } : null;
    case 'chi': {
      const chosen = option ?? claims.chi[0];
      return chosen === undefined ? null : { type: 'chi', seat, option: chosen };
    }
  }
}

/** Applies the winning claim to the engine (mirrors the free-play BotDriver). */
export function applyWinner(state: MahjongState, winner: Claim): MahjongState {
  switch (winner.type) {
    case 'ron':
      return declareRon(state, winner.seat);
    case 'pung':
      return claimPung(state, winner.seat);
    case 'chi':
      return claimChi(state, winner.seat, winner.option);
  }
}
